// sms-config.js — SMS Self-Serve Portal reference data + pure helpers.
//
// Ported from the standalone Alpine prototype (window.SMS) to plain ESM exports.
// Single source of truth for labels, copy, mock data, and the pure functions
// that derive from them. No state, no DOM, no framework code.
//
// NOTE: utility-class strings below are emitted into markup, so Tailwind's JIT
// purge scans this file (see tailwind.config.js `purge.content`). Opacity-slash
// utilities (Tailwind 3) were rewritten to Tailwind-2 `*-opacity-*` form.

// ── Window / building / weather reference ────────────────────────────────
export const WINDOW_LABELS = {
  morning: "8:00 AM – 12:00 PM",
  afternoon: "12:00 PM – 5:00 PM",
  evening: "5:00 PM – 8:00 PM"
};

export const BUILDING_LABELS = {
  house: "House",
  townhouse: "Townhouse",
  apartment: "Apartment",
  business: "Business",
  hospital: "Hospital"
};

export const DROP_NOTES = {
  house: "left at your front door",
  townhouse: "left at your front door",
  apartment: "left in the lobby — check with reception if needed",
  business: "left at reception",
  hospital: "checked in at reception"
};

export const WEATHER_NOTES = {
  perfect: "Perfect weather ahead — they'll look gorgeous.",
  hot: "It's a warm one — we'll keep your blooms cool until delivery.",
  cold: "Cold day ahead — your blooms will be wrapped up warm."
};

export const WEATHER_LABELS = {
  perfect: "Perfect 🌤",
  hot: "Hot ☀️",
  cold: "Cold 🌨"
};

// ── Insert-variable chips (single source for editor + bulk) ───────────────
export const EDITOR_VARS = [
  "{{recipient_name}}",
  "{{order_number}}",
  "{{address}}",
  "{{delivery_window}}",
  "{{alias}}",
  "{{sender_name}}",
  "{{driver_name}}",
  "{{driver_notes}}",
  "{{photo_link}}",
  "{{care_link}}"
];
export const BULK_VARS = [
  "{{recipient_name}}",
  "{{order_number}}",
  "{{delivery_window}}",
  "{{address}}",
  "{{sender_name}}"
];

// ── Flow Library (Callia Core, Phase 1) ───────────────────────────────────
// dims = the variation dimensions shown as small chips on each card.
export const FLOW_LIBRARY = {
  recipientDriver: [
    {
      name: "Heads-up",
      desc:
        "Day-before or morning-of notice that a Callia driver delivery is coming",
      badges: ["Recipient", "Driver"],
      dims: ["consent", "order timing", "weather", "building type"],
      meta: "1 message · multiple variations",
      edited: "Edited 2d ago"
    },
    {
      name: "ETA",
      desc: "Driver is en route — estimated arrival window sent to recipient",
      badges: ["Recipient", "Driver"],
      dims: ["consent", "building type"],
      meta: "1 message · multiple variations",
      edited: "Edited 2d ago"
    },
    {
      name: "Delivered",
      desc: "Delivery confirmed — drop-off location varies by building type",
      badges: ["Recipient", "Driver"],
      dims: ["consent", "building type"],
      meta: "1 message · multiple variations",
      edited: "Edited 4d ago"
    },
    {
      name: "3-Day follow-up",
      desc: "Post-delivery check-in sent 3 days after Callia driver delivery",
      badges: ["Recipient", "Driver"],
      dims: ["consent"],
      meta: "1 message",
      edited: "Trigger: +3 days post-delivery"
    }
  ],
  recipientCourier: [
    {
      name: "Heads-up",
      desc: "Advance notice that a courier delivery is on its way",
      badges: ["Recipient", "Courier"],
      dims: ["consent", "order timing", "weather", "building type"],
      meta: "1 message · multiple variations",
      edited: "Edited 5d ago"
    },
    {
      name: "Delivered",
      desc: "Courier delivery confirmed — drop location note by building type",
      badges: ["Recipient", "Courier"],
      dims: ["consent", "building type"],
      meta: "1 message · multiple variations",
      edited: "Edited 5d ago"
    },
    {
      name: "3-Day follow-up",
      desc: "Post-delivery check-in sent 3 days after courier delivery",
      badges: ["Recipient", "Courier"],
      dims: ["consent"],
      meta: "1 message",
      edited: "Trigger: +3 days post-delivery"
    }
  ],
  sender: [
    {
      name: "Heads-up / ETA",
      desc: "Notifies sender when their order is out for delivery, with ETA",
      badges: ["Sender", "Driver · Courier"],
      dims: ["order timing"],
      meta: "1 message",
      edited: "Edited 3d ago"
    },
    {
      name: "Delivered",
      desc: "Confirms to sender that their flowers were successfully delivered",
      badges: ["Sender", "Driver · Courier"],
      dims: ["building type"],
      meta: "1 message",
      edited: "Edited 3d ago"
    }
  ]
};

// Small coloured dimension chips on flow cards (light + dark tints).
export const DIM_CHIP_CLASS = {
  consent:
    "bg-emerald-50 text-emerald-700 dark:bg-emerald-900 dark:bg-opacity-30 dark:text-emerald-300",
  "order timing":
    "bg-amber-50 text-amber-700 dark:bg-amber-900 dark:bg-opacity-30 dark:text-amber-300",
  weather:
    "bg-indigo-50 text-indigo-700 dark:bg-indigo-900 dark:bg-opacity-30 dark:text-indigo-300",
  "building type":
    "bg-purple-50 text-purple-700 dark:bg-purple-900 dark:bg-opacity-30 dark:text-purple-300"
};

// ── Editor: message types ─────────────────────────────────────────────────
export const MESSAGE_TYPES = [
  {
    key: "heads-up",
    label: "Heads-up",
    sub: "has variations",
    editorTitle: "Heads-up — Callia Driver · Recipient",
    hasVariations: true
  },
  {
    key: "eta",
    label: "ETA",
    sub: "has variations",
    editorTitle: "ETA — Callia Driver · Recipient",
    hasVariations: true
  },
  {
    key: "delivered",
    label: "Delivered",
    sub: "single message",
    editorTitle: "Delivered — Callia Driver · Recipient",
    hasVariations: false
  },
  {
    key: "followup",
    label: "3-day follow-up",
    sub: "single message",
    editorTitle: "3-Day Follow-up — Callia Driver · Recipient",
    hasVariations: false
  }
];

// ── Editor: variation matrix (building × timing × weather) ────────────────
export const BUILDINGS = [
  { key: "House", icon: "🏠", groupId: "grp-house" },
  { key: "Townhouse", icon: "🏘", groupId: "grp-town" },
  { key: "Apartment", icon: "🏢", groupId: "grp-apt" },
  { key: "Business", icon: "🏬", groupId: "grp-biz" },
  { key: "Hospital", icon: "🏥", groupId: "grp-hosp" }
];
export const TIMINGS = ["Morning", "Afternoon"];
export const WEATHERS = ["Perfect", "Hot", "Cold"];

// Only this one variation is an unpublished draft; the rest are live.
const VARIATION_DRAFTS = new Set(["House/Morning/Perfect"]);

// Build the full list of variations for one building (pure).
export function buildVariations(building) {
  const rows = [];
  for (const timing of TIMINGS) {
    for (const weather of WEATHERS) {
      const key = `${building}/${timing}/${weather}`;
      rows.push({
        building,
        timing,
        weather,
        key,
        status: VARIATION_DRAFTS.has(key) ? "draft" : "live"
      });
    }
  }
  return rows;
}

// ── Editor: sample message content per variation / per type ───────────────
export const VARIATION_CONTENT = {
  "House/Morning/Perfect":
    "Hi {{recipient_name}}, your Callia flowers are arriving today/tomorrow! 🌷 We'll be at your door between {{delivery_window}}. Perfect weather for flowers — see you soon!",
  "House/Morning/Hot":
    "Hi {{recipient_name}}, your Callia flowers are arriving today/tomorrow! 🌸 We'll be there between {{delivery_window}}. It's a warm one — we'll keep your blooms in the shade until delivery.",
  "House/Morning/Cold":
    "Hi {{recipient_name}}, your Callia flowers are arriving today/tomorrow! 🌷 We'll be there between {{delivery_window}}. Cold day ahead — we'll make sure your blooms are wrapped up warm.",
  "House/Afternoon/Perfect":
    "Hi {{recipient_name}}, your Callia flowers are on their way this afternoon! 🌷 Expect your delivery between {{delivery_window}}.",
  "Apartment/Morning/Perfect":
    "Hi {{recipient_name}}, your Callia flowers arrive today/tomorrow! 🌸 We'll be at {{address}} between {{delivery_window}}. Please make sure lobby access is available or let us know your buzzer.",
  "Hospital/Morning/Perfect":
    "Hi {{recipient_name}}, a beautiful Callia delivery is on its way to you today/tomorrow! 🌷 We'll be arriving between {{delivery_window}}. Our driver will check in at reception."
};
export const DEFAULT_VARIATION_KEY = "House/Morning/Perfect";

export const MSG_CONTENT_BY_TYPE = {
  "heads-up":
    "Hi {{recipient_name}}, your Callia flowers are arriving today! 🌷 We'll be at your door between {{delivery_window}}. Perfect weather for flowers — see you soon!",
  eta:
    "Hi {{recipient_name}}, your Callia driver {{driver_name}} is on the way! 🚗 Estimated arrival: {{delivery_window}}.",
  delivered:
    "We've delivered your Callia box! {{driver_notes}}Here is a quick peek: {{photo_link}}. Check out these tips to help your flowers last! {{care_link}}",
  followup:
    "Hi {{recipient_name}}! 🌷 Hope you're still enjoying your Callia flowers. How are they looking? We'd love to hear from you — reply anytime!"
};

// The live version used for the editor diff (vs. the working draft).
export const EDITOR_LIVE_VERSION =
  "Hi {{recipient_name}}, your Callia flowers are arriving today! 🌷 We'll be at your door between {{delivery_window}}.";

// Editor version history (display only).
export const EDITOR_VERSIONS = [
  {
    current: true,
    user: "You · Draft (current)",
    meta: "Today 11:42 AM · Edited House/Morning/Perfect"
  },
  {
    current: false,
    user: "Jade M. · Published v4",
    meta: "May 29 · Timing 7:30→8:00 AM"
  },
  {
    current: false,
    user: "Jade M. · Published v3",
    meta: "Apr 14 · Content rewrite"
  }
];

// ── Simulator: mock Shopify orders ────────────────────────────────────────
export const MOCK_ORDERS = {
  "100482": {
    title: "#100482",
    recipient: "Sarah Chen",
    type: "driver",
    building: "house",
    window: "afternoon",
    consent: "yes",
    timing: "before",
    windowLabel: "12:00 PM – 5:00 PM"
  },
  "100391": {
    title: "#100391",
    recipient: "James Park",
    type: "courier",
    building: "apartment",
    window: "morning",
    consent: "no",
    timing: "after-b1",
    windowLabel: "8:00 AM – 12:00 PM"
  },
  "100455": {
    title: "#100455",
    recipient: "Priya Mehta",
    type: "driver",
    building: "business",
    window: "morning",
    consent: "yes",
    timing: "after-b2",
    windowLabel: "8:00 AM – 12:00 PM"
  }
};

// ── Triggers ──────────────────────────────────────────────────────────────
export const TRIGGER_DEFAULTS = {
  batch1: {
    modalTitle: "Edit trigger — Batch 1 heads-up",
    time: "10:00",
    day: "day-before"
  },
  batch2: {
    modalTitle: "Edit trigger — Batch 2 heads-up",
    time: "08:00",
    day: "day-of"
  },
  followup: {
    modalTitle: "Edit trigger — 3-day follow-up",
    time: "10:00",
    day: "day-of"
  }
};

export const STATIC_TRIGGERS = {
  batch3: {
    when: "At order pull · Final cutoff for order placing",
    word: "today",
    source: "🔗 Linked to order pull event"
  },
  eta: {
    fires: "Onfleet route is started",
    note: "No time offset — sends immediately at trigger",
    source: "🔗 Onfleet webhook"
  },
  delivered: {
    fires: "Shopify order is fulfilled",
    note: "No time offset — sends immediately at trigger",
    source: "🔗 Shopify fulfillment webhook"
  }
};

export const TRIGGER_CHANGELOG = [
  {
    current: true,
    user: "Jade M. · Published",
    meta: "May 29 · Batch 1 cutoff: 9:00 AM → 10:00 AM"
  },
  {
    current: false,
    user: "Alyssa D. · Published",
    meta: "Mar 12 · Batch 2: 7:30 AM → 8:00 AM"
  },
  {
    current: false,
    user: "Dev · Initial setup",
    meta: "Feb 3 · All triggers configured"
  }
];

// ── Bulk Send ─────────────────────────────────────────────────────────────
export const BULK_TEMPLATES = [
  {
    name: "Valentine's heads-up",
    preview: "Pre-delivery notice for Feb 14 orders with gift context"
  },
  {
    name: "B2B delivery notice",
    preview: "Business account pre-delivery with order summary"
  },
  {
    name: "Delay notice",
    preview: "Apologetic delay alert with revised window"
  },
  { name: "Custom message", preview: "Write from scratch" }
];
export const BULK_DEFAULT_MSG = `Hi {{recipient_name}} 🌹 A Valentine's Day delivery from Callia is on its way to you tomorrow, Feb 14! Your order (#{{order_number}}) will arrive between {{delivery_window}}.

Reply STOP to unsubscribe.`;

// ── Permissions: team members (mock) ──────────────────────────────────────
// avatarClass = Tailwind utilities for the round avatar (bg + text colour).
// portal = the permission tier resolved at sign-in (viewer | editor | publisher).
export const TEAM_MEMBERS = [
  {
    initials: "AD",
    avatarClass: "bg-brand-teal text-white",
    name: "Alyssa D.",
    email: "alyssa@calliaflowers.com",
    role: "Operations Manager",
    portal: "publisher",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "AS",
    avatarClass: "bg-brand-pink text-white",
    name: "Amira S.",
    email: "amira@calliaflowers.com",
    role: "Operations Specialist",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "CK",
    avatarClass: "bg-brand-yellow text-brand-teal-dark",
    name: "Caitlyn K.",
    email: "caitlyn@calliaflowers.com",
    role: "Business Operations Lead",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "CM",
    avatarClass: "bg-brand-light-blue text-brand-teal-dark",
    name: "Catherine M.",
    email: "catherine@calliaflowers.com",
    role: "MDAA",
    portal: "publisher",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "CL",
    avatarClass:
      "bg-brand-light-grey text-gray-600 dark:bg-dark-border dark:text-dark-text",
    name: "Chrishane L.",
    email: "chrishane@calliaflowers.com",
    role: "Dev Lead",
    portal: "publisher",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "DK",
    avatarClass: "bg-brand-teal text-white",
    name: "Dustin K.",
    email: "dustin@calliaflowers.com",
    role: "CX Specialist",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "EF",
    avatarClass: "bg-brand-pink text-white",
    name: "Eli F.",
    email: "eli@calliaflowers.com",
    role: "Dev",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "HL",
    avatarClass: "bg-brand-yellow text-brand-teal-dark",
    name: "Huaijun L.",
    email: "huaijun@calliaflowers.com",
    role: "Dev",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "JB",
    avatarClass: "bg-brand-light-blue text-brand-teal-dark",
    name: "Jaclyn B.",
    email: "jaclyn@calliaflowers.com",
    role: "COO",
    portal: "publisher",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "JC",
    avatarClass:
      "bg-brand-light-grey text-gray-600 dark:bg-dark-border dark:text-dark-text",
    name: "Jessi C.",
    email: "jessi@calliaflowers.com",
    role: "CX Specialist",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "JR",
    avatarClass: "bg-brand-teal text-white",
    name: "Johnny R.",
    email: "johnny@calliaflowers.com",
    role: "Growth Lead",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "JP",
    avatarClass: "bg-brand-pink text-white",
    name: "Julian P.",
    email: "julian@calliaflowers.com",
    role: "Supply Chain Specialist",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "KB",
    avatarClass: "bg-brand-yellow text-brand-teal-dark",
    name: "Karla B.",
    email: "karla@calliaflowers.com",
    role: "Operations Specialist",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "LE",
    avatarClass: "bg-brand-light-blue text-brand-teal-dark",
    name: "Lama E.",
    email: "lama@calliaflowers.com",
    role: "Finance Specialist",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "LO",
    avatarClass:
      "bg-brand-light-grey text-gray-600 dark:bg-dark-border dark:text-dark-text",
    name: "Louise",
    email: "louise@calliaflowers.com",
    role: "CX Specialist",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "MR",
    avatarClass: "bg-brand-teal text-white",
    name: "Meaghan R.",
    email: "meaghan@calliaflowers.com",
    role: "Integration Lead",
    portal: "editor",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "ME",
    avatarClass: "bg-brand-pink text-white",
    name: "Mohamed E.",
    email: "mohamed@calliaflowers.com",
    role: "Operations Specialist",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "NR",
    avatarClass: "bg-brand-yellow text-brand-teal-dark",
    name: "Natasha R.",
    email: "natasha@calliaflowers.com",
    role: "Marketing Lead",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "RP",
    avatarClass: "bg-brand-light-blue text-brand-teal-dark",
    name: "Roby P.",
    email: "roby@calliaflowers.com",
    role: "Operations Specialist",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "SK",
    avatarClass:
      "bg-brand-light-grey text-gray-600 dark:bg-dark-border dark:text-dark-text",
    name: "Shahmir K.",
    email: "shahmir@calliaflowers.com",
    role: "Dev",
    portal: "viewer",
    scope: "All BUs",
    active: "—"
  },
  {
    initials: "VA",
    avatarClass: "bg-brand-teal text-white",
    name: "Valentina",
    email: "valentina@calliaflowers.com",
    role: "CX Manager",
    portal: "editor",
    scope: "All BUs",
    active: "—"
  }
];

export const ROLE_GUIDE = [
  {
    role: "Viewer",
    accent: "",
    desc:
      "Read-only. Can browse flows and run simulations. Cannot save drafts or publish."
  },
  {
    role: "Editor",
    accent: "border-l-4 border-brand-yellow",
    desc:
      "Can create and save drafts, submit for review. Cannot push changes live."
  },
  {
    role: "Publisher",
    accent: "border-l-4 border-brand-teal",
    desc:
      "Full access. Can review submitted drafts and publish flows live to Twilio."
  }
];

// ── GSM-7 character sets (Twilio-spec) ────────────────────────────────────
const GSM7_CHARS = new Set([
  "@",
  "£",
  "$",
  "¥",
  "è",
  "é",
  "ù",
  "ì",
  "ò",
  "Ç",
  "\n",
  "Ø",
  "ø",
  "\r",
  "Å",
  "å",
  "Δ",
  "_",
  "Φ",
  "Γ",
  "Λ",
  "Ω",
  "Π",
  "Ψ",
  "Σ",
  "Θ",
  "Ξ",
  "Æ",
  "æ",
  "ß",
  "É",
  " ",
  "!",
  '"',
  "#",
  "¤",
  "%",
  "&",
  "'",
  "(",
  ")",
  "*",
  "+",
  ",",
  "-",
  ".",
  "/",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  ":",
  ";",
  "<",
  "=",
  ">",
  "?",
  "¡",
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "Ä",
  "Ö",
  "Ñ",
  "Ü",
  "§",
  "¿",
  "a",
  "b",
  "c",
  "d",
  "e",
  "f",
  "g",
  "h",
  "i",
  "j",
  "k",
  "l",
  "m",
  "n",
  "o",
  "p",
  "q",
  "r",
  "s",
  "t",
  "u",
  "v",
  "w",
  "x",
  "y",
  "z",
  "ä",
  "ö",
  "ñ",
  "ü",
  "à"
]);
const GSM7_EXT = new Set(["{", "}", "\\", "[", "]", "~", "|", "^", "€"]);

// ── Pure helper: calculate SMS segments (Twilio-spec) ─────────────────────
export function calcSegments(text) {
  const cleaned = text.replace(/\{\{[^}]+\}\}/g, "VARIABLE");
  let isGSM7 = true;
  let charCount = 0;
  for (const ch of cleaned) {
    if (GSM7_EXT.has(ch)) {
      charCount += 2;
    } else if (GSM7_CHARS.has(ch)) {
      charCount += 1;
    } else {
      isGSM7 = false;
      break;
    }
  }
  if (!isGSM7) {
    const len = [...cleaned].length;
    const single = 70,
      multi = 67;
    const segs = len <= single ? 1 : Math.ceil(len / multi);
    const remaining = segs === 1 ? single - len : segs * multi - len;
    return {
      encoding: "UCS-2",
      chars: len,
      segments: segs,
      remaining,
      limit: segs === 1 ? single : multi
    };
  }
  const single = 160,
    multi = 153;
  const segs = charCount <= single ? 1 : Math.ceil(charCount / multi);
  const remaining = segs === 1 ? single - charCount : segs * multi - charCount;
  return {
    encoding: "GSM-7",
    chars: charCount,
    segments: segs,
    remaining,
    limit: segs === 1 ? single : multi
  };
}

// ── Pure helper: segment reduction suggestions ────────────────────────────
export function suggestReduction(text, info) {
  const suggestions = [];
  if (info.encoding === "UCS-2" && /\p{Emoji}/u.test(text)) {
    suggestions.push(
      "Remove emoji — emoji forces UCS-2, cutting segment size from 160 to 70 chars. Removing them drops back to GSM-7 and likely saves a segment."
    );
  }
  if (/[‘’“”]/.test(text)) {
    suggestions.push(
      "Replace “curly” quotes with straight quotes — curly quotes force UCS-2 encoding on the entire message."
    );
  }
  if (info.segments >= 2) {
    const cleaned = text.replace(/\{\{[^}]+\}\}/g, "VARIABLE");
    const overage =
      info.encoding === "GSM-7"
        ? info.chars - (info.segments - 1) * 153
        : [...cleaned].length - (info.segments - 1) * 67;
    if (overage <= 25) {
      suggestions.push(
        `You're ${overage} chars into segment ${info.segments}. Trimming that much saves a full segment and reduces cost.`
      );
    }
  }
  return suggestions;
}

// ── Pure helper: format a trigger time + day into a display label ─────────
export function formatTriggerTime(time, day) {
  const [h, m] = time.split(":");
  const hr = parseInt(h, 10);
  const ampm = hr >= 12 ? "PM" : "AM";
  const hr12 = hr % 12 || 12;
  const dayLabel =
    day === "day-before" ? "Day before delivery" : "Delivery day";
  return `${hr12}:${m} ${ampm} · ${dayLabel} · local time`;
}

// ── Pure helper: capitalise first letter ──────────────────────────────────
export function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
